import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {FormatSettingsSection} from 'src/components/format-settings';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {PersonalizationTabs} from '../personalization-tabs';

import {useFormatSettings} from './format-settings/useFormatSettings';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';

const TAB_NAME = 'View & Preferences';

type Props = {
    userId: number,
    accountId: number,
};

export const ViewPreferences = ({userId, accountId}: Props) => {
    const {canEditUser} = useAdminAccessRight();
    const {userSubmit, userFieldChange} = useEventTracking();
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);
    const [busy, setBusy] = useState<boolean>(false);

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-view-and-preferences',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const onFieldChange = (section: string) => (parameterName: string) => {
        userFieldChange({
            section,
            viewType: 'Existed',
            parameterName,
        });
    };

    const {
        loadingError: localeLoadingError,
        isLoading: isLocaleLoading,
        isChanged: isLocaleChanged,
        data: locale,
        dataInitial: defaultLocale,
        setData: setLocale,
        saveChanges: saveLocale,
        discardChanges: discardLocale,
    } = useFormatSettings({userId});

    useEffect(() => {
        setIsChunkChanged(isLocaleChanged);
    }, [isLocaleChanged, setIsChunkChanged]);

    const saveChanges = useCallback(async (options?: SaveOptions) => {
        const calls = [];

        setBusy(true);
        try {

            if (isLocaleChanged) {
                calls.push(await saveLocale(options));
            }
            setBusy(false);

            const errors = [...new Set(calls.filter(Boolean))];
            if (errors.length) {
                setErrorMessage(errors.join('\n'));
                return errors.join('\n');
            } else {
                proceed();
            }
            userSubmit({submit: true, place: TAB_NAME});
        } finally {
            setBusy(false);
        }
    }, [
        isLocaleChanged,
        saveLocale,
        proceed,
        setErrorMessage,
        userSubmit,
    ]);

    const discardChanges = useCallback(() => {
        discardLocale();

        userSubmit({submit: false, place: TAB_NAME});
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardLocale, setSuccessMessage, userSubmit]);

    const editDisabled = useMemo(() => !canEditUser(accountId), [canEditUser, accountId]);

    return (
        <>
            <PersonalizationTabs userId={userId} activeTab="view-preferences"/>
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            {TAB_NAME}
                        </Typography>
                        {!editDisabled && (
                            <Group>
                                <Button
                                    variant="Type.SECONDARY"
                                    onClick={discardChanges}
                                    disabled={busy || !isCurrentChunkChanged}>
                                    Discard Changes
                                </Button>
                                <SaveWithReasonButton
                                    busy={busy}
                                    disabled={!isCurrentChunkChanged}
                                    onSave={saveChanges}
                                />
                            </Group>
                        )}
                    </Group>
                </Item>
            )}>
                <FormatSettingsSection
                    disabled={editDisabled}
                    isLoading={isLocaleLoading}
                    loadingError={localeLoadingError}
                    onChange={setLocale}
                    locale={locale}
                    defaultLocale={defaultLocale}
                    onFieldChange={onFieldChange('Format Settings')}
                    withTimeZone={true}
                />
            </ProfileMain>
        </>
    );
};
